import type { Event, EventListener } from 'three';
import { Object3D } from 'three';

import { TransformControls } from '@/lib/three';
import { RawPoints, Sight } from '@/utils/render';

import { Stage } from './Stage';

// transformControl z 值范围 buffer
const CONTROL_Z_BUFFER = 10;

export class TransformControlHandler {
  public controls?: TransformControls;
  private stage: Stage;
  private currentSight?: Sight;

  constructor(stage: Stage) {
    this.stage = stage;
  }

  private createControls() {
    if (!this.stage.currentSight) return;
    this.controls = new TransformControls(this.stage.currentSight.camera, this.stage.canvas);
    this.controls.size = 0.8;
    this.controls.showXZ = false;
    this.controls.showYZ = false;

    this.changeSight();
  }

  // 切换 sight 时，根据 stage.curentSight 更新 controls
  public changeSight() {
    if (this.controls && this.stage.currentSight) {
      this.currentSight = this.stage.currentSight;
      this.controls.camera = this.currentSight.camera;
      if (this.currentSight.rawObject instanceof RawPoints && this.currentSight.rawObject.zRange) {
        this.controls.minZ = this.currentSight.rawObject.zRange[0] - CONTROL_Z_BUFFER;
        this.controls.maxZ = this.currentSight.rawObject.zRange[1] + CONTROL_Z_BUFFER;
      }
      this.controls.getRaycaster().layers.set(Sight.MARKER_LAYER);
      this.currentSight.addMarker(this.controls);
    }
  }

  public update(target?: Object3D, supportRotate = false) {
    if (!target) {
      this.controls?.detach();
      return;
    }
    if (!this.controls) {
      this.createControls();
    }
    if (this.stage.currentSight && this.currentSight !== this.stage.currentSight) {
      this.changeSight();
    }
    this.controls?.attach(target);
    if (supportRotate) {
      this.controls?.setMode('translateAndRotate');
      this.controls?.setSpace('local');
    }
  }

  public mute(status: boolean) {
    if (this.controls) {
      this.controls.enabled = !status;
    }
  }

  public dispose() {
    if (this.controls) {
      this.controls.parent?.remove(this.controls);
      this.controls.dispose();
    }
  }

  public onChange(eventHandler: EventListener<Event, 'change', TransformControls>) {
    if (this.controls) {
      this.controls.addEventListener('change', eventHandler);
    }
  }

  get dragging() {
    return this.controls?.dragging;
  }

  get visible() {
    return this.controls?.visible ?? false;
  }

  get hasAxis() {
    return this.controls?.axis !== null;
  }

  /**
   * 判断当前轴控制器是否处于响应态
   */
  get isActive() {
    return this.visible && this.hasAxis;
  }
}
